import makeStyles from '@material-ui/styles/makeStyles';
import React, { Dispatch, Reducer, ReducerAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdvancedChecklistItem from './AdvancedChecklistItem';
import { AdvancedChecklistStateType } from './state/AdvancedChecklist';
import { ReactComponent as ArrowIconSVG } from '../../../../assets/images/icon-arrow-down.svg';
import { ReactComponent as ClearIconSVG } from '../../../../assets/images/icon-clear.svg';
import SearchInput from './SearchInput';
import { FilterOptionsType } from './state/AdvancedChecklist.actions';
import { setAdvancedChecklistSelectedFilters } from './state';

export interface IAdvancedChecklistFilters {
    state: AdvancedChecklistStateType;
    dispatch: Dispatch<ReducerAction<Reducer<any, any>>>;
    persistSelectedFilters?: boolean;
}

const useStyles = makeStyles({
    AdvancedChecklistFilters: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginTop: '0.937rem',
    },
    FilterSectionDiv: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        backgroundColor: '#F3F4F9',
        borderRadius: '8px',
        padding: '0.5rem 0.9375rem',
        '&:hover': {
            cursor: 'pointer',
        },
        '&>p': {
            fontSize: '0.875rem',
            color: '#737FA4',
            margin: '0 0.625rem 0 0',
        },
    },
    FilterSectionDivActive: {
        backgroundColor: '#455681',
        borderRadius: '8px',
        '&>p': {
            color: '#FFFFFF',
            fontWeight: 600,
        },
        '&>svg': {
            transform: 'rotate(180deg)',
            '&>path': {
                fill: '#FFFFFF',
                stroke: '#FFFFFF',
            },
        },
    },
    openedFilterSection: {
        position: 'absolute',
        zIndex: 99999,
        width: '12.5rem',
        top: '120%',
        boxShadow: '0px 2px 15px rgba(33, 33, 33, 0.15)',
        borderRadius: '8px',
        height: '20rem',
        background: '#FFFFFF',
        '& input': {
            border: 'none',
            width: '100%',
            height: '100%',
            padding: '0.625rem 0.9375rem',
            backgroundColor: 'transparent',
            '&:focus': {
                outlineColor: '#FFDD02',
                outlineWidth: '0.5px',
            },
        },
        '&>div:first-child': {
            height: '10%',
        },
        '& >div:nth-child(2)': {
            height: '80%',
            borderBottom: '1px solid #E8EAF4',
            borderTop: '1px solid #E8EAF4',
            overflowX: 'hidden',
            overflowY: 'auto',
        },
        '& >div:nth-child(3)': {
            height: '10%',
            display: 'flex',
            justifyContent: 'flex-start',
            paddingLeft: '0.937rem',
            alignItems: 'center',
            '&>p': {
                fontSize: '0.75rem',
                margin: '0',
                color: '#455681',
                marginLeft: '0.625rem',
            },
            '&:hover': {
                cursor: 'pointer',
            },
        },
    },
    SearchInput: {
        position: 'relative',
        borderRadius: '8px',
        height: '100%',
        '& input': {
            width: '100%',
            height: '100%',
            padding: '0.625rem 0.9375rem 0.625rem 2.25rem',
            backgroundColor: 'transparent',
            // borderTopRightRadius:"8px",
            // borderTopLeftRadius:"8px",
            '&:focus': {
                outlineColor: '#FFDD02',
                outlineWidth: '0.5px',
            },
        },
        '& >svg': {
            position: 'absolute',
            left: '0.9375rem',
            top: '0',
            bottom: '0',
            margin: 'auto',
        },
    },
});
function AdvancedChecklistFilters(props: IAdvancedChecklistFilters) {
    const { t } = useTranslation();
    const classes = useStyles();
    const [openedFilter, setOpenedFilter] = useState<string | undefined>(undefined);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedFiltersIDs, setSelectedFiltersIDs] = useState<string[]>([]);
    const { state, dispatch } = props;
    useEffect(() => {
        setSelectedFiltersIDs([...state.selectedFiltersIDs]);
        // eslint-disable-next-line
    }, [props.state]);

    function selectFilter(id: string) {
        let filtersIDs = state.selectedFiltersIDs;
        const IdIndex = filtersIDs.indexOf(id);
        if (IdIndex > -1) {
            filtersIDs.splice(IdIndex, 1);
        } else {
            filtersIDs.push(id);
        }
        dispatch(setAdvancedChecklistSelectedFilters([...filtersIDs]));
        if (props.persistSelectedFilters) {
            window.localStorage.setItem('selectedFiltersIDs', JSON.stringify(filtersIDs));
        }
    }

    function clearFilters() {
        dispatch(setAdvancedChecklistSelectedFilters([]));
    }

    const renderFilterOptions = (item: FilterOptionsType) => {
        return (
            <div>
                {item.filterOptionsList.map((option: any) => {
                    if (option[item.optionLabelKey]?.toLowerCase().includes(searchValue.toLowerCase())) {
                        return (
                            <div style={{ height: '30px' }} data-component-name="SubcategoryFilterOption">
                                <AdvancedChecklistItem
                                    idKey={'id'}
                                    selected={selectedFiltersIDs.includes(option.id)}
                                    itemData={option}
                                    mainLabelKey={item.optionLabelKey}
                                    onClick={() => selectFilter(option[item.optionFilterKey])}
                                />
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        );
    };

    const renderOpenedFilterComponent = (item: FilterOptionsType) => {
        return (
            <div className={classes.openedFilterSection}>
                <SearchInput classes={classes} onSearchChange={setSearchValue} />
                {renderFilterOptions(item)}
                <ClearSelectionComponent />
            </div>
        );
    };
    const ClearSelectionComponent = () => {
        return (
            <div onClick={clearFilters}>
                <ClearIconSVG width="0.75rem" height="0.75rem" />
                <p>{t('filters.clearSelection')}</p>
            </div>
        );
    };
    return (
        <div className={classes.AdvancedChecklistFilters}>
            {props?.state?.filtersList?.map((item: FilterOptionsType) => {
                return (
                    <div style={{ position: 'relative' }}>
                        <div
                            className={`${classes.FilterSectionDiv} ${
                                item.label === openedFilter ? classes.FilterSectionDivActive : ''
                            }`}
                            onClick={() => setOpenedFilter(item.label === openedFilter ? '' : item.label)}
                        >
                            <p>{item.label}</p>
                            <ArrowIconSVG width="12px" height="6px" fill="white" />
                        </div>
                        {item.label === openedFilter && renderOpenedFilterComponent(item)}
                    </div>
                );
            })}
        </div>
    );
}

export default React.memo(AdvancedChecklistFilters);
